/**
 * Utility methods for managing Course Materials
 */
import moment from 'moment';
import _ from 'underscore';

import { getBranchesForCourse } from 'bundles/author-branches/service/branches';
import { getBranchIdForSession as getBranchIdForSessionUtil } from 'bundles/author-branches/utils/BranchUtils';
import { generateElementId } from 'bundles/author-common/utils/IdUtils';
import ItemTypes from 'bundles/author-course/constants/ItemTypes';
import TrackIds from 'bundles/author-course/constants/TrackIds';
import type CourseMaterial from 'bundles/author-course/models/CourseMaterial';
import type Item from 'bundles/author-course/models/Item';
import type Lesson from 'bundles/author-course/models/Lesson';
import type Module from 'bundles/author-course/models/Module';
import { PeerReviewTypes } from 'bundles/author-peer-review/constants/PeerReviewTypes';
import { getUnversionedAtomId } from 'bundles/authoring/common/utils/ItemSharingUtils';
import type { AuthoringAtomsV2 } from 'bundles/naptimejs/resources/__generated__/AuthoringAtomsV2';

import _t from 'i18n!nls/author-course';

const getTranslation = (key: string) => {
  switch (key) {
    case 'module':
      return _t('Untitled Module');
    case 'lesson':
      return _t('Untitled Lesson');
    case 'peer':
      return _t('Peer Assignment');
    case 'quiz':
      return _t('Quiz');
    case 'video':
      return _t('Video');
    case 'programming':
      return _t('Programming Assignment');
    case 'sga':
      return _t('Assignment');
    case 'practiceAssignment':
      return _t('Assignment');
    default:
      return '';
  }
};

const CourseMaterialUtils = {
  /**
   * Return true if a module can be modified and deleted.
   */
  canModifyAndDeleteModule(module: Module, branchIsLaunched: boolean) {
    if (!branchIsLaunched) {
      return true;
    }

    const items = CourseMaterialUtils.getModuleItems(module);
    return !_.find(items, (item) => !CourseMaterialUtils.canModifyAndDeleteItem(item, branchIsLaunched));
  },

  canModifyAndDeleteLesson(lesson: Lesson, branchIsLaunched: boolean) {
    if (!branchIsLaunched) {
      return true;
    }

    const items = CourseMaterialUtils.getLessonItems(lesson);
    return !_.find(items, (item) => !CourseMaterialUtils.canModifyAndDeleteItem(item, branchIsLaunched));
  },

  canModifyAndDeleteItem(item: Item, branchIsLaunched: boolean) {
    if (!branchIsLaunched) {
      return true;
    }

    const itemType = _(ItemTypes).find((type) => type.typeName === item.content.typeName);
    return itemType && !itemType.graded;
  },

  /**
   * Return a skeleton module object
   */
  createNewModule() {
    return {
      id: generateElementId(),
      name: getTranslation('module'),
      description: '',
      elements: [],
    };
  },

  /**
   * Return a skeleton lesson object
   */
  createNewLesson() {
    return {
      id: generateElementId(),
      name: getTranslation('lesson'),
      description: '',
      elements: [],
      trackId: TrackIds.CORE,
    };
  },

  /**
   * Return a skeleton item object
   */
  createNewItem(itemType: string) {
    const defaultName = '';
    let definition = {};

    switch (itemType) {
      case ItemTypes.SUMMATIVE_QUIZ.typeName:
      case ItemTypes.FORMATIVE_QUIZ.typeName:
        definition = { questionCount: 0 };
        break;

      default:
        break;
    }

    return {
      id: generateElementId(),

      name: defaultName,
      published: false,
      hasDraft: true,

      content: {
        typeName: itemType,
        definition,
      },
    };
  },

  getFlattenedModuleLessons(module: Module) {
    return _(module.elements).chain().flatten().value();
  },

  getLessonItems(lesson: Lesson) {
    return _(lesson.elements).chain().flatten().value();
  },

  getModuleItems(module: Module) {
    return _(module.elements).chain().map(CourseMaterialUtils.getLessonItems).flatten().value();
  },

  getCourseMaterialItems(courseMaterial: CourseMaterial) {
    return _(courseMaterial.elements).chain().map(CourseMaterialUtils.getModuleItems).flatten().value();
  },

  getHonorsLessonsFromCourseMaterial(courseMaterial: CourseMaterial) {
    return CourseMaterialUtils.getHonorsTrackLessons(
      _(courseMaterial.elements).chain().map(CourseMaterialUtils.getFlattenedModuleLessons).flatten().value()
    );
  },

  getPublishedItemsFromCourseMaterial(courseMaterial: CourseMaterial) {
    return CourseMaterialUtils.getCourseMaterialItems(courseMaterial).filter((item) => item.published);
  },

  getUnpublishedItemsFromCourseMaterial(courseMaterial: CourseMaterial) {
    return CourseMaterialUtils.getCourseMaterialItems(courseMaterial).filter((item) => !item.published);
  },

  getGradableItemsFromCourseMaterial(courseMaterial: CourseMaterial) {
    return CourseMaterialUtils.getCourseMaterialItems(courseMaterial).filter((item) =>
      CourseMaterialUtils.isGradableItem(item.content.typeName)
    );
  },

  isGradableItem(typeName: string | undefined = '') {
    const itemType = _(ItemTypes).find((type) => type.typeName === typeName);
    return itemType && itemType.graded;
  },

  getPublishedItemType(courseMaterial: CourseMaterial, itemId: string) {
    const publishedItem = CourseMaterialUtils.getCourseMaterialItems(courseMaterial).find(
      (item) => item.id === itemId && item.published
    );

    return publishedItem && publishedItem.content.typeName;
  },

  getPublishedItemTypeFromDraftType(draftItemType?: string | undefined) {
    switch (draftItemType) {
      case PeerReviewTypes.GRADED.typeName:
        return PeerReviewTypes.GRADED.publishedTypeName;
      case PeerReviewTypes.STANDARD.typeName:
        return PeerReviewTypes.STANDARD.publishedTypeName;
      case PeerReviewTypes.PHASED.typeName:
        return PeerReviewTypes.PHASED.publishedTypeName;
      case PeerReviewTypes.CLOSED.typeName:
        return PeerReviewTypes.CLOSED.publishedTypeName;
      case 'graded':
        return 'gradedProgramming';
      case 'ungraded':
        return 'ungradedProgramming';
      default:
        return draftItemType;
    }
  },

  getBreakingChangePublishStates(
    isBreakingChangeAllowed: boolean,
    publishedItemType?: string | undefined,
    draftItemType?: string | undefined
  ) {
    const publishedTypeOfDraft = CourseMaterialUtils.getPublishedItemTypeFromDraftType(draftItemType);
    const publishedAndDraftItemTypesAreDifferent = publishedTypeOfDraft !== publishedItemType;
    const hasNewGradedItem =
      (!publishedItemType || !CourseMaterialUtils.isGradableItem(publishedItemType)) &&
      CourseMaterialUtils.isGradableItem(publishedTypeOfDraft);
    const hasNewGradedness =
      hasNewGradedItem ||
      (CourseMaterialUtils.isGradableItem(publishedItemType) &&
        !CourseMaterialUtils.isGradableItem(publishedTypeOfDraft));

    const hasBreakingChange =
      isBreakingChangeAllowed && (hasNewGradedItem || (!!publishedItemType && publishedAndDraftItemTypesAreDifferent));

    return {
      hasBreakingChange,
      hasNewGradedItem,
      hasNewGradedness,
    };
  },

  getIsRequiredItem(courseMaterial: CourseMaterial, itemId: string) {
    return (
      !!courseMaterial &&
      !!courseMaterial.elementsRequiredForPassing &&
      _(courseMaterial.elementsRequiredForPassing).contains(`item~${itemId}`)
    );
  },

  getIsAnonymizedGradingEnabled(courseMaterial: CourseMaterial, itemId: string) {
    return (
      !!courseMaterial &&
      !!courseMaterial.gradingParameters &&
      !!courseMaterial.gradingParameters.anonymouslyGradedItems &&
      _(courseMaterial.gradingParameters.anonymouslyGradedItems).contains(itemId)
    );
  },

  /**
   * Check if parentModule has only one published item with id = itemId
   *
   * @param {object} parentModule - parent module of specified item
   * @param {string} itemId - item id of specified item
   * @return {boolean} - is item with id = itemId the only published item in parent module
   */
  isOnlyPublishedItem(parentModule?: Module, itemId?: string) {
    if (parentModule) {
      const publishedItems = parentModule.items.filter((item) => item.published === true);
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      if (publishedItems.length === 1 && publishedItems[0].id === itemId) {
        return true;
      }
    }
    return false;
  },

  /**
   * check if all published items in parentModule are in the lesson with id = lessonId
   *
   * @param {object} parentModule - parent module of specified lesson
   * @param {string} lessonId - lesson id of specified lesson
   * @return {boolean} - is lesson with id = lessonId the only lesson in parent module that contains published items
   */
  isOnlyLessonWithPublishedItem(parentModule?: Module, lessonId?: string) {
    if (parentModule) {
      return parentModule.items.filter((item) => item.published === true).every((item) => item.lessonId === lessonId);
    }
    return false;
  },

  isItemPassable(isCumulativeGraded: boolean, isRequiredItem: boolean) {
    return !isCumulativeGraded || isRequiredItem;
  },

  getCoreTrackLessons(lessons: Array<Lesson>) {
    return lessons.filter(this.isCoreLesson);
  },

  getHonorsTrackLessons(lessons: Array<Lesson>) {
    return lessons.filter(this.isHonorsLesson);
  },

  isCoreLesson(lesson: Lesson) {
    return lesson.trackId === TrackIds.CORE;
  },

  isHonorsLesson(lesson: Lesson) {
    return lesson.trackId === TrackIds.HONORS;
  },

  setTrackForLessons(lessons: Array<Lesson>, trackId: string) {
    lessons.forEach((lesson) => {
      const _lesson = lesson;
      _lesson.trackId = trackId;
    });
  },

  getBranchIdForSession(courseId: string, sessionId: string) {
    return getBranchesForCourse(courseId).then((branches) => {
      return getBranchIdForSessionUtil(sessionId, branches);
    });
  },

  getHoursAndMinutes(milliseconds: number | string | undefined) {
    const duration = moment.duration(milliseconds);

    const seconds = duration.seconds();
    const minutes = duration.minutes() + Math.round(seconds / 60);
    const hours = duration.hours() + duration.days() * 24;

    return {
      hours,
      minutes,
    };
  },

  getMinutesFromMilliseconds(milliseconds: number | undefined) {
    const { hours, minutes } = CourseMaterialUtils.getHoursAndMinutes(milliseconds);

    return hours * 60 + minutes;
  },

  /**
   * Transform a time in milliseconds to one in hours and minutes.
   * - 1h 2m
   * - 40m
   * - 0m
   * -
   *
   * @deprecated Do not use this function; it's i18n-unsafe!
   *
   * @return {string} Human readable form of estimated time, for display in authoring
   */
  humanizeTime(milliseconds?: number | string) {
    const { hours, minutes } = CourseMaterialUtils.getHoursAndMinutes(milliseconds);

    if (hours && minutes) {
      return `${hours}h ${minutes}m`;
    } else if (hours) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  },

  getTimeInMilliseconds(hours?: number | undefined, minutes?: number | undefined) {
    return moment.duration({ hours, minutes }).asMilliseconds();
  },

  getModuleItemSummaryTypeNames() {
    const typeNames = {};
    [
      ItemTypes.STANDARD_PEER.typeName,
      ItemTypes.CLOSED_PEER.typeName,
      ItemTypes.PHASED_PEER.typeName,
      ItemTypes.GRADED_PEER.typeName,
    ].forEach((type) => {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      typeNames[type] = getTranslation('peer');
    });
    [ItemTypes.SUMMATIVE_QUIZ.typeName, ItemTypes.FORMATIVE_QUIZ.typeName].forEach((type) => {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      typeNames[type] = getTranslation('quiz');
    });
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    typeNames[ItemTypes.LECTURE.typeName] = getTranslation('video');
    [
      ItemTypes.PROGRAMMING.typeName,
      ItemTypes.GRADED_PROGRAMMING.typeName,
      ItemTypes.UNGRADED_PROGRAMMING.typeName,
    ].forEach((type) => {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      typeNames[type] = getTranslation('programming');
    });
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    typeNames[ItemTypes.STAFF_GRADED.typeName] = getTranslation('sga');
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    typeNames[ItemTypes.PRACTICE_ASSIGNMENT.typeName] = getTranslation('practiceAssignment');

    return typeNames;
  },

  getModuleItemSummaries(modules: Array<Module>) {
    const itemSummaryTypeNames = CourseMaterialUtils.getModuleItemSummaryTypeNames();
    const moduleSummaries = {};

    modules.forEach((module) => {
      const numItemsPerCategory = {};
      const lessons = module.elements;
      lessons.forEach((lesson) => {
        const items = CourseMaterialUtils.getLessonItems(lesson);

        items.forEach((item) => {
          let type = item.content.typeName;
          // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          if (itemSummaryTypeNames[type]) {
            // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            type = itemSummaryTypeNames[type];
            // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            if (numItemsPerCategory[type]) {
              // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
              numItemsPerCategory[type] += 1;
            } else {
              // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
              numItemsPerCategory[type] = 1;
            }
          }
        });
      });

      let summary = '';
      _(numItemsPerCategory)
        .keys()
        .forEach((key) => {
          // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          summary += key + ': ' + numItemsPerCategory[key] + ', ';
        });
      summary = summary.replace(/,\s$/, '');
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      moduleSummaries[module.id] = { name: module.name, summary };
    });

    return moduleSummaries;
  },

  getAuthoringAtomForAtomId(atomId: string, authoringAtoms: Array<AuthoringAtomsV2>): AuthoringAtomsV2 | {} {
    if (atomId && authoringAtoms) {
      return authoringAtoms.find((atom) => atom.id === atomId) || {};
    }

    return {};
  },

  getEmbeddedSourceCourseIdForAtomId(
    atomId: string,
    authoringAtomRelations: Array<{ id: string; embeddedContentSourceCourseId?: string }>
  ): { embeddedContentSourceCourseId?: string } | {} {
    if (atomId && authoringAtomRelations) {
      const unversionedAtomId = getUnversionedAtomId(atomId);
      const atomRelation = authoringAtomRelations.find((atom) => atom.id === unversionedAtomId);
      if (atomRelation && atomRelation.embeddedContentSourceCourseId) {
        return { embeddedContentSourceCourseId: atomRelation.embeddedContentSourceCourseId };
      }

      return {};
    }

    return {};
  },

  isExportsAllowed: (courseMaterial: CourseMaterial): boolean => {
    return !!courseMaterial.modules.length;
  },
};

export default CourseMaterialUtils;

export const {
  canModifyAndDeleteModule,
  canModifyAndDeleteLesson,
  canModifyAndDeleteItem,
  createNewModule,
  createNewLesson,
  createNewItem,
  getFlattenedModuleLessons,
  getLessonItems,
  getModuleItems,
  getCourseMaterialItems,
  getPublishedItemsFromCourseMaterial,
  getUnpublishedItemsFromCourseMaterial,
  getGradableItemsFromCourseMaterial,
  isGradableItem,
  getPublishedItemType,
  getPublishedItemTypeFromDraftType,
  getBreakingChangePublishStates,
  getIsRequiredItem,
  getIsAnonymizedGradingEnabled,
  isExportsAllowed,
  isOnlyPublishedItem,
  isOnlyLessonWithPublishedItem,
  isItemPassable,
  getCoreTrackLessons,
  getHonorsTrackLessons,
  isCoreLesson,
  isHonorsLesson,
  setTrackForLessons,
  getBranchIdForSession,
  getHoursAndMinutes,
  getMinutesFromMilliseconds,
  humanizeTime,
  getTimeInMilliseconds,
  getModuleItemSummaryTypeNames,
  getModuleItemSummaries,
  getHonorsLessonsFromCourseMaterial,
  getAuthoringAtomForAtomId,
  getEmbeddedSourceCourseIdForAtomId,
} = CourseMaterialUtils;
